import { createTheme } from '@material-ui/core';
import { defaultTooltipStylingReset } from './defaultTooltipStylingReset';

export enum TerraColor {
  // Grays
  White = '#FFFFFF',
  Gray10 = '#F8FAFA',
  Gray20 = '#DEE6E6',
  Gray40 = '#B0C1C5',
  Gray60 = '#617575',
  Gray80 = '#3D4949',
  Gray100 = '#212829',

  // Teals
  Teal10 = '#D5F2F2',
  Teal80 = '#447476',
  Teal100 = '#1E3D3F',

  // Greens
  Green10 = '#E3F5D5',
  Green40 = '#619D54',
  Green80 = '#266119',

  // Yellows
  Yellow20 = '#FFEAA1',
  Yellow40 = '#FDB839',
  Yellow80 = '#474128',

  // Reds
  Red20 = '#FFDADA',
  Red40 = '#EF5B57',
  Red60 = '#B70505',

  // Secondary Colors: Should just be used for Brand Health
  // Purples
  Purple10 = '#E3CEFF',
  Purple40 = '#A076D7',
  Purple80 = '#401D6E',

  // Avocado
  Avocado10 = '#EAFCBE',
  Avocado40 = '#9DC62E',
  Avocado80 = '#5D6E00',

  // Orange
  Orange10 = '#FFD2B6',
  Orange40 = '#FD8439',
  Orange80 = '#893E15',

  // Accent colors
  Stone10 = '#F8F8F8',
  Stone40 = '#A7A7A7',
  Stone80 = '#454545',
  Treetrunk10 = '#F6F2EC',
  Treetrunk80 = '#594B39',
  Reef10 = '#FFEDE8',
  Reef80 = '#A42D07',
  Ocean10 = '#DFE6F8',
  Ocean80 = '#2F406C',
  Forest10 = '#E3FAE0',
  Forest80 = '#31622A',
  Rose10 = '#FEE3F0',
  Rose80 = '#874965',
  Redrock10 = '#FFDCDC',
  Redrock80 = '#962A2A',
  Pollen10 = '#FFE7C1',
  Pollen80 = '#875A17',
  Lavender10 = '#E7E0EE',
  Lavender80 = '#755990',
}

export const getTheme = () => {
  const baseFont = { fontWeight: 400, letterSpacing: 'normal' };
  return createTheme({
    // Matched with marketplace media queries
    breakpoints: {
      values: {
        xs: 0,
        sm: 550,
        md: 768,
        lg: 1024,
        xl: 1921,
      },
    },
    palette: {
      primary: {
        light: TerraColor.Teal10,
        main: TerraColor.Teal80,
        dark: TerraColor.Teal100,
        contrastText: TerraColor.White,
      },
      secondary: {
        light: TerraColor.Gray10,
        main: TerraColor.Gray80,
        dark: TerraColor.Gray100,
        contrastText: TerraColor.White,
      },
      error: {
        light: TerraColor.Red20,
        main: TerraColor.Red40,
        dark: TerraColor.Red60,
      },
      warning: {
        light: TerraColor.Yellow20,
        main: TerraColor.Yellow40,
        contrastText: TerraColor.Yellow80,
      },
      success: {
        light: TerraColor.Green10,
        main: TerraColor.Green40,
        dark: TerraColor.Green80,
      },
    },
    typography: {
      fontFamily: 'TT Commons !important',
      h1: { ...baseFont, fontSize: '2rem' },
      h2: { ...baseFont, fontSize: '1.5rem' },
      h3: { ...baseFont, fontSize: '1rem' },
      h4: { ...baseFont, fontSize: '1rem' },
      h5: { ...baseFont, fontSize: '1rem' },
      h6: { ...baseFont, fontSize: '1rem' },
      subtitle1: { ...baseFont, fontSize: '1rem', textTransform: 'uppercase' },
      subtitle2: { ...baseFont, fontSize: '0.75rem', textTransform: 'uppercase' },
      body1: { ...baseFont, fontSize: '1rem' },
      body2: { ...baseFont, fontSize: '0.75rem' },
      button: {
        textTransform: 'none' as any,
      },
    },
    /**
     * Switch to `components` syntax once we're on MUI v5
     * https://linear.app/treet/issue/TREET-9845/upgrade-material-ui-to-5
     * https://mui.com/material-ui/customization/theme-components/#theme-style-overrides
     */
    overrides: {
      // Change caption component defaults
      MuiTable: {
        root: {
          '& caption': {
            captionSide: 'top',
            // Allow us to use tailwind for padding
            padding: undefined,
          },
        },
      },
      MuiTableCell: {
        // Remove default bold from table headers
        head: {
          fontWeight: undefined,
        },
        // Remove default padding from small table cells
        sizeSmall: {
          '&:last-child': {
            paddingRight: undefined,
          },
        },
      },
      MuiChip: {
        root: {
          '& *': {
            fontFamily: 'TT Commons !important',
          },
        },
        sizeSmall: {
          padding: '2px 8px',
          height: 'auto',
          borderRadius: '120px',
          fontSize: '.5rem',
          fontWeight: 500,
        },
        label: {
          padding: '0 !important',
        },
      },
      ...defaultTooltipStylingReset,
    },
  });
};
